import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  GenericAbortSignal,
  InternalAxiosRequestConfig,
} from "axios";

import i18n from "../i18n";
import { networkSlice } from "../store/networkSlice";
import { setApiError } from "../store/reducers/errorSlice";
import {
  pinRequired,
  sessionEnded,
  sessionLocked,
} from "../store/reducers/sessionSlice";
import { store } from "../store/store";
import { errorType } from "../types/error.definition";
import { getApplicationApiUrl, getLoginApiUrl } from "../utils/config-utils";
import {
  dispatchError,
  errorCategoryMapper,
  isSilentError,
  isValidErrorFormat,
} from "../utils/errors-utils";

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    /** MED-1734: the single automatic GET retry has been spent. */
    __retried?: boolean;
    /** MED-1734: lets the slow-connection banner abort this request. */
    __controller?: AbortController;
  }
}

// MED-1734: patients are mostly on 4G. Traefik cuts the server-side chain at
// 20 s (the BFF then answers 504 GATEWAY_TIMEOUT), so the browser must wait
// longer than that to receive the typed error rather than time out itself.
export const REQUEST_TIMEOUT_MS = 30_000;
// Budget for a request carrying a file or data URI. No Axios call does so today
// (the profile photo goes to Firebase; the API only receives its URL) — wrap
// the request config in withUploadTimeout() when one appears.
export const UPLOAD_TIMEOUT_MS = 60_000;
export const RETRY_DELAY_MS = 1_500;

export const withUploadTimeout = <T extends AxiosRequestConfig>(
  config: T
): T => ({ ...config, timeout: UPLOAD_TIMEOUT_MS });

// Primary API client
const API_CONFIG = {
  timeout: REQUEST_TIMEOUT_MS,
  ...(getApplicationApiUrl() && { baseURL: getApplicationApiUrl() }),
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

// Secondary API client
const LOGIN_API_CONFIG = {
  timeout: REQUEST_TIMEOUT_MS,
  ...(getLoginApiUrl() && { baseURL: getLoginApiUrl() }),
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);
const loginApiClient = axios.create(LOGIN_API_CONFIG);

// ── MED-1734: network resilience ─────────────────────────────────────────────

type TransportErrorCode = "TIMEOUT_ERROR" | "OFFLINE_ERROR" | "NETWORK_ERROR";

const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Failures that never reached the application: the browser gave up (timeout,
 * no network) or Traefik/the BFF gave up waiting for the API (504). Anything
 * else carries a body the business branch of the interceptor knows how to read
 * (a 503 DATABASE_TIMEOUT included).
 */
const classifyTransportError = (
  error: AxiosError
): TransportErrorCode | null => {
  if (
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT ||
    error.response?.status === 504
  ) {
    return "TIMEOUT_ERROR";
  }
  if (!error.response) return isOffline() ? "OFFLINE_ERROR" : "NETWORK_ERROR";
  return null;
};

// One automatic retry, GET only, and only when nothing answered: a 4xx/5xx
// with a body is the server's decision, and offline would just fail fast again.
const canRetry = (error: AxiosError) => {
  const config = error.config;
  return (
    config !== undefined &&
    !config.__retried &&
    !error.response &&
    (config.method ?? "").toLowerCase() === "get" &&
    !isOffline()
  );
};

const delay = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

const settleTransportError = async (
  error: AxiosError,
  code: TransportErrorCode,
  client: AxiosInstance,
  quiet: boolean
): Promise<AxiosResponse> => {
  if (error.config && canRetry(error)) {
    error.config.__retried = true;
    await delay(RETRY_DELAY_MS);
    return client.request(error.config);
  }
  const formattedError = {
    code,
    category: errorType.RETRIABLE_UNEXPECTED_ERROR,
    message: error.message,
  };
  if (!quiet) dispatchError(formattedError, false);
  return Promise.reject(formattedError);
};

// Every request gets its own AbortController so the slow-connection banner can
// cancel it; a signal the caller passed (search autocomplete) still aborts it.
const linkAbortController = (callerSignal?: GenericAbortSignal) => {
  const controller = new AbortController();
  if (callerSignal?.aborted) controller.abort();
  else callerSignal?.addEventListener?.("abort", () => controller.abort());
  return controller;
};

const trackRequest = (config: InternalAxiosRequestConfig) => {
  // A retry reuses the controller it was given on the first attempt.
  const controller = config.__controller ?? linkAbortController(config.signal);
  config.__controller = controller;
  config.signal = controller.signal;
  networkSlice.requestStarted(controller);
};

const untrackRequest = (config?: InternalAxiosRequestConfig) =>
  networkSlice.requestEnded(config?.__controller);

// Offline: fail at once with OFFLINE_ERROR instead of waiting out the timeout.
// Rejecting here skips the adapter and lands in the response error handler.
const rejectOffline = (config: InternalAxiosRequestConfig) =>
  Promise.reject(
    new AxiosError("Network Error", AxiosError.ERR_NETWORK, config)
  );

// ─────────────────────────────────────────────────────────────────────────────

// Add request interceptor to include session token and handle errors
apiClient.interceptors.request.use(
  config => {
    if (isOffline()) return rejectOffline(config);
    trackRequest(config);
    const state = store.getState();
    const sessionToken = state.session.sessionToken;
    if (sessionToken) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
    }
    // Tell the backend which language the user selected so locale-sensitive
    // responses and emails (confirmations, reminders, etc.) are translated.
    config.headers["Accept-Language"] = i18n.language || "fr";
    return config;
  },
  error => {
    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      const formattedError = errorCategoryMapper(errorResponse);
      if (!isSilentError(formattedError)) {
        dispatchError(formattedError, false);
      }
      return Promise.reject({ ...formattedError });
    } else {
      dispatchError(error, true);
      return Promise.reject({
        code: "BUSINESS_ERROR",
        category: "BUSINESS_ERROR",
      });
    }
  }
);

// Requests whose failure must never open the global error modal:
//  - validate-session: 401 means session expired/invalid, fail silently
//  - payments/open (MED-1320): background lookup on every licence page load;
//    a failure only means "no payment in progress" and the page carries on
//  - account/activity (MED-1620): fire-and-forget heartbeat; a 423 still
//    locks the screen (see notifyPinLock)
// Note: axios rewrites config.url to the full URL before interceptors run,
// so we must use endsWith rather than strict equality.
const isQuietRequest = (url?: string) =>
  url !== undefined &&
  (url.endsWith("/validate-session") ||
    url.endsWith("/payments/open") ||
    url.endsWith("/account/activity"));

// PIN lock (MED-1620): the server refuses every call of a locked session (423)
// or of a user without a PIN (403); the lock screen takes over from there.
// The server can also end the session for security reasons: the user is told
// why, then sent to the login page (SessionEndedNotice).
const notifyPinLock = (code: string) => {
  if (code === "SESSION_LOCKED") store.dispatch(sessionLocked());
  else if (code === "PIN_REQUIRED") store.dispatch(pinRequired());
  else if (
    code === "PIN_CREATE_REAUTH_REQUIRED" ||
    code === "SESSION_REUSE_DETECTED"
  )
    store.dispatch(sessionEnded(code));
};

// Add interceptors to primary instance
apiClient.interceptors.response.use(
  response => {
    untrackRequest(response.config);
    return response;
  },
  async error => {
    untrackRequest(error?.config);
    // Cancelled requests (AbortController: a newer search, or "Annuler" on the
    // slow-connection banner) never open the modal.
    if (axios.isCancel(error)) {
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
    const transportCode = classifyTransportError(error);
    if (transportCode) {
      return settleTransportError(
        error,
        transportCode,
        apiClient,
        isQuietRequest(error?.config?.url)
      );
    }
    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      const formattedError = errorCategoryMapper(errorResponse);
      notifyPinLock(formattedError.code);
      // Only dispatch to Redux store if not a silent error

      if (
        !isSilentError(formattedError) &&
        !isQuietRequest(error.response.config.url)
      ) {
        dispatchError(formattedError, false);
      }
      return Promise.reject({ ...formattedError });
    } else {
      // Skip modal for quiet requests (see isQuietRequest)
      if (!isQuietRequest(error?.config?.url)) {
        dispatchError(error, true);
      }
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

// Add interceptors to secondary instance if needed
loginApiClient.interceptors.response.use(
  response => {
    untrackRequest(response.config);
    return response;
  },
  async error => {
    untrackRequest(error?.config);
    if (axios.isCancel(error)) {
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
    const transportCode = classifyTransportError(error);
    if (transportCode) {
      return settleTransportError(error, transportCode, loginApiClient, false);
    }
    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      const formattedError = errorCategoryMapper(errorResponse);
      // Only dispatch to Redux store if not a silent error
      if (!isSilentError(formattedError)) {
        dispatchError(formattedError, false);
      }
      return Promise.reject({ ...formattedError });
    } else {
      dispatchError(error, true);
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

loginApiClient.interceptors.request.use(
  config => {
    if (isOffline()) return rejectOffline(config);
    trackRequest(config);
    // Forward the selected language to the login BFF/API too (activation,
    // password-reset and other login-flow emails are locale-sensitive).
    config.headers["Accept-Language"] = i18n.language || "fr";
    return config;
  },
  error => {
    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      const formattedError = errorCategoryMapper(errorResponse);
      // Only dispatch to Redux store if not a silent error
      if (!isSilentError(formattedError)) {
        store.dispatch(setApiError(formattedError));
      }
      return Promise.reject({ ...formattedError });
    } else {
      dispatchError(error, true);
      return Promise.reject({
        code: "BUSINESS_ERROR",
        category: "BUSINESS_ERROR",
      });
    }
  }
);

export default { apiClient, loginApiClient };
